import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal, Quote, Star } from "lucide-react";
import { BEFORE_AFTER } from "@/data/content";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const Slider = ({ item }) => {
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  const dragging = useRef(false);
  const interacted = useRef(false);
  const rafRef = useRef(null);
  const startT = useRef(0);

  useEffect(() => {
    const measure = () => ref.current && setWidth(ref.current.offsetWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    },
    [setFromClientX]
  );

  const stopAuto = useCallback(() => {
    interacted.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const stop = useCallback(() => {
    dragging.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("mouseup", stop);
    window.removeEventListener("touchend", stop);
  }, [onMove]);

  const start = useCallback(() => {
    stopAuto();
    dragging.current = true;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
  }, [onMove, stop, stopAuto]);

  // Auto-sweep the divider while the slider is visible (until user interacts)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const loop = (t) => {
      if (interacted.current || dragging.current) return;
      if (!startT.current) startT.current = t;
      const elapsed = (t - startT.current) / 1000;
      // smooth oscillation between ~12% and ~88%, 6s period
      setPos(50 + 38 * Math.sin((elapsed * Math.PI * 2) / 6));
      rafRef.current = requestAnimationFrame(loop);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !interacted.current) {
            startT.current = 0;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(loop);
          } else if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`ba-slider-${item.id}`}
      className="relative w-full aspect-[3/2] overflow-hidden rounded-sm select-none cursor-ew-resize bg-ink"
      onClick={(e) => {
        stopAuto();
        setFromClientX(e.clientX);
      }}
    >
      <img
        src={item.after}
        alt={`${item.label} après`}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={item.before}
          alt={`${item.label} avant`}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: width ? `${width}px` : "100%" }}
          draggable={false}
        />
        <span className="absolute top-3 left-3 bg-ink/80 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
          Avant
        </span>
      </div>
      <span className="absolute top-3 right-3 bg-white text-ink text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
        Après
      </span>

      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <button
          data-testid={`ba-handle-${item.id}`}
          onMouseDown={start}
          onTouchStart={start}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white rounded-full shadow-lg cursor-ew-resize pointer-events-auto"
        >
          <MoveHorizontal className="w-5 h-5 text-ink" />
        </button>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const [active, setActive] = useState(0);
  const item = BEFORE_AFTER[active];

  return (
    <section
      id="resultats"
      data-testid="before-after-gallery"
      className="relative overflow-hidden py-20 md:py-28 bg-background scroll-mt-44"
    >
      <AnimatedLines />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Preuve par l'image
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-3">
            Résultats detailing & nettoyage à Belfort, Montbéliard, Héricourt
          </h2>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg">
            Le curseur se déplace automatiquement — glissez-le pour comparer vous-même.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {BEFORE_AFTER.map((b, i) => (
            <button
              key={b.id}
              data-testid={`ba-tab-${b.id}`}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-sm text-sm font-bold transition-colors ${
                active === i
                  ? "bg-ink text-white"
                  : "bg-white text-ink border border-border hover:border-ink"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>

        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center"
        >
          <Slider item={item} />

          <div className="bg-white p-8 rounded-sm border border-border">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {item.category}
            </span>
            <div className="flex gap-1 my-4">
              {["r1", "r2", "r3", "r4", "r5"].map((k) => (
                <Star key={k} className="w-5 h-5 fill-ink text-ink" />
              ))}
            </div>
            <Quote className="w-9 h-9 text-marble-dark" />
            <blockquote className="font-heading text-xl md:text-2xl font-bold text-ink leading-snug mt-2">
              “{item.quote}”
            </blockquote>
            <p className="text-sm font-semibold text-muted-foreground mt-4">
              — {item.author}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
